import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  KeyRound,
  Loader2,
  ShieldCheck,
  Smartphone,
} from "lucide-react";
import { AuthSidePanel } from "../components/AuthSidePanel";
import { BrandMark } from "../components/BrandMark";
import { ApiRequestError, api } from "../lib/api";

export function ForgotPasswordPage() {
  const requestPath = "/api/auth/password-reset/request";
  const verifyPath = "/api/auth/password-reset/verify";
  const loginPath = "/login";

  const [step, setStep] = useState<"username" | "reset" | "done">("username");
  const [username, setUsername] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (!cooldown) return;
    const timer = setTimeout(() => setCooldown((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  async function sendCode() {
    await api<{ sent: boolean }>(requestPath, {
      method: "POST",
      body: JSON.stringify({ username: username.trim().toLowerCase() }),
    });
    setStep("reset");
    setCode("");
    setCooldown(60);
  }

  async function handleRequest(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await sendCode();
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Tidak dapat terhubung ke server. Coba lagi.",
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleReset(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    if (!/^\d{6}$/.test(code.trim())) {
      setError("Kode OTP harus 6 digit.");
      return;
    }
    if (password.length < 8) {
      setError("Password baru minimal 8 karakter.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Konfirmasi password belum sama.");
      return;
    }

    setLoading(true);
    try {
      await api<{ reset: boolean }>(verifyPath, {
        method: "POST",
        body: JSON.stringify({
          username: username.trim().toLowerCase(),
          code: code.trim(),
          new_password: password,
        }),
      });
      setStep("done");
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Tidak dapat terhubung ke server. Coba lagi.",
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    if (cooldown > 0) return;
    setError(null);
    setLoading(true);
    try {
      await sendCode();
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Tidak dapat mengirim ulang kode.",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-shell">
      <AuthSidePanel
        eyebrow="Pemulihan akun"
        title={
          <>
            Lupa password.
            <br />
            Verifikasi lewat
            <br />
            <em>WhatsApp.</em>
          </>
        }
        lead="Masukkan username owner atau superadmin, lalu verifikasi kode OTP yang dikirim ke nomor WhatsApp akun. Tidak perlu email."
        features={[
          { icon: ShieldCheck, label: "Owner & superadmin" },
          { icon: Smartphone, label: "OTP WhatsApp" },
          { icon: KeyRound, label: "Sesi dicabut" },
        ]}
        footer="Kode OTP berlaku 5 menit, maksimal 5 percobaan, dan hanya bisa dipakai sekali."
      />

      <main className="auth-main">
        <div className="auth-card auth-card--wide">
          <Link to="/" className="auth-card__brand auth-card__brand--link">
            <BrandMark size="sm" />
            <span>Cafe System Control</span>
          </Link>

          <Link to="/" className="auth-back">
            <ArrowLeft size={14} /> Kembali ke beranda
          </Link>

          <p className="eyebrow">
            {step === "done" ? "Selesai" : "Pemulihan akun"}
          </p>
          <h2>
            {step === "username"
              ? "Reset password"
              : step === "reset"
                ? "Verifikasi kode"
                : "Password diperbarui"}
          </h2>
          <p className="auth-subtitle">
            {step === "username"
              ? "Masukkan username akun. Kami kirim kode OTP ke WhatsApp yang terhubung."
              : step === "reset"
                ? `Masukkan kode 6 digit dan password baru untuk ${username}.`
                : "Semua sesi lama sudah dicabut. Silakan masuk dengan password baru."}
          </p>

          {error ? (
            <div className="alert alert--danger" role="alert">
              <AlertCircle size={17} />
              <span>{error}</span>
            </div>
          ) : null}

          {step === "username" ? (
            <form className="auth-form" onSubmit={handleRequest}>
              <label className="field">
                <span>Username</span>
                <input
                  className="input"
                  type="text"
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  placeholder="Masukkan username akun"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  minLength={3}
                  maxLength={64}
                  required
                />
              </label>

              <button
                type="submit"
                className="btn btn-primary btn-block"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="spin" size={17} />
                    Memproses...
                  </>
                ) : (
                  <>
                    Kirim kode WhatsApp
                    <ArrowRight size={17} />
                  </>
                )}
              </button>

              <p className="auth-footnote">
                Jika username terdaftar, kode dikirim ke WhatsApp yang
                terhubung. <Link className="auth-link" to={loginPath}>Kembali ke login</Link>
              </p>
            </form>
          ) : null}

          {step === "reset" ? (
            <form className="auth-form" onSubmit={handleReset}>
              <label className="field">
                <span>Kode OTP 6 digit</span>
                <input
                  className="input otp-input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  placeholder="000000"
                  value={code}
                  onChange={(event) =>
                    setCode(event.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  maxLength={6}
                  required
                />
              </label>

              <label className="field">
                <span>Password baru</span>
                <input
                  className="input"
                  type="password"
                  autoComplete="new-password"
                  placeholder="Minimal 8 karakter"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  minLength={8}
                  maxLength={256}
                  required
                />
              </label>

              <label className="field">
                <span>Konfirmasi password baru</span>
                <input
                  className="input"
                  type="password"
                  autoComplete="new-password"
                  placeholder="Ulangi password baru"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  minLength={8}
                  maxLength={256}
                  required
                />
              </label>

              <button
                type="submit"
                className="btn btn-primary btn-block"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="spin" size={17} />
                    Menyimpan...
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={17} />
                    Simpan password baru
                  </>
                )}
              </button>

              <div className="auth-links">
                <button
                  type="button"
                  className="btn btn-ghost btn--sm"
                  onClick={() => setStep("username")}
                  disabled={loading}
                >
                  <ArrowLeft size={15} />
                  Ubah username
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn--sm"
                  onClick={() => void handleResend()}
                  disabled={loading || cooldown > 0}
                >
                  {cooldown > 0 ? `Kirim ulang (${cooldown}s)` : "Kirim ulang"}
                </button>
              </div>
            </form>
          ) : null}

          {step === "done" ? (
            <div className="auth-form">
              <div className="alert alert--success" role="status">
                <CheckCircle2 size={17} />
                <span>Password berhasil diperbarui. Silakan masuk kembali.</span>
              </div>
              <Link className="btn btn-primary btn-block" to={loginPath}>
                Ke halaman login
                <ArrowRight size={17} />
              </Link>
            </div>
          ) : null}
        </div>
      </main>
    </div>
  );
}
